import axios from "../api/axios"

export default {
  namespaced: true,
  state: {
    auth: false,
    patient: null,
    hospitalCode: null,
    protocolNo: null,
    birthdate: null,
    sessionId: null,
    laboratoryReports: [],
    radiologyReports: [],
    pathologyReports: [],
    cardiologyReports: [],
    otherProcessReports: []
  },
  mutations: {
    reset(state) {
      state.auth = false
      state.patient = null
      state.hospitalCode = null
      state.protocolNo = null
      state.birthdate = null
      state.sessionId = null

      state.laboratoryReports = []
      state.radiologyReports = []
      state.pathologyReports = []
      state.cardiologyReports = []
      state.otherProcessReports = []
    },
    auth(state, data) {
      state.auth = data
    },
    patient(state, data) {
      state.patient = data.patient || null
      state.hospitalCode = data.hospitalCode || null
      state.protocolNo = data.protocolNo || null
      state.birthdate = data.birthdate || null
      state.sessionId = data.sessionId || null
    },
    laboratoryReports(state, data) {
      state.laboratoryReports = data
    },
    radiologyReports(state, data) {
      state.radiologyReports = data
    },
    pathologyReports(state, data) {
      state.pathologyReports = data
    },
    cardiologyReports(state, data) {
      state.cardiologyReports = data
    },
    otherProcessReports(state, data) {
      state.otherProcessReports = data
    }
  },
  getters: {
    auth: state => {
      return state.auth
    },
    patient: state => {
      return {
        name: state.patient,
        hospitalCode: state.hospitalCode,
        protocolNo: parseInt(state.protocolNo),
        birthdate: this_.$moment(state.birthdate).format("YYYY-MM-DD"),
        sessionId: state.sessionId
      }
    },
    laboratoryReports: state => {
      return state.laboratoryReports.map(rec => {
        return {
          reportId: rec.reportId,
          reportType: rec.reportType,
          title: `${rec.workGroups} - ${rec.reportTests[0].operatorDepartment}`,
          date: rec.approvalDate || rec.processDate || rec.demandDate,
          status: rec.status
        }
      })
    },
    radiologyReports: state => {
      return state.radiologyReports.map(rec => {
        return {
          reportId: rec.reportId,
          reportType: rec.reportType,
          title: `${rec.processName} - ${rec.demanderDepartment}`,
          doctor: rec.doctor.toUpperCase(),
          date: rec.approvalDate || rec.processDate || rec.demandDate,
          status: rec.status
        }
      })
    },
    pathologyReports: state => {
      return state.pathologyReports.map(rec => {
        return {
          reportId: rec.reportId,
          reportType: rec.reportType,
          title: `${rec.requestedByDepartment} - ${rec.processName}`,
          doctor: rec.requestedByDoctor.toUpperCase(),
          date: rec.approvalDate || rec.sampleDate
        }
      })
    },
    cardiologyReports: state => {
      return state.cardiologyReports.map(rec => {
        return {
          reportId: rec.reportId,
          reportType: rec.reportType,
          title: `${rec.processName}`,
          doctor: rec.doctorName.toUpperCase(),
          date: rec.approvalDate || rec.processDate || rec.demandDate
        }
      })
    },
    otherProcessReports: state => {
      return state.otherProcessReports.map(rec => {
        return {
          reportId: rec.reportId,
          reportType: rec.reportType,
          title: `${rec.processName} - ${rec.departmentName}`,
          doctor: rec.doctorName.toUpperCase(),
          date: rec.approvalDate || rec.processDate || rec.demandDate
        }
      })
    },
    reports: (state, { laboratoryReports, radiologyReports, pathologyReports, cardiologyReports, otherProcessReports }) => {
      return { laboratoryReports, radiologyReports, pathologyReports, cardiologyReports, otherProcessReports }
    }
  },
  actions: {
    init({ dispatch, state, getters, rootState }) {
      if (getters.auth) return true

      state.patient = rootState.guest?.name
      state.hospitalCode = rootState.guest?.vip?.replace("hospital=", "")
      state.protocolNo = rootState.guest?.pms_id
      state.birthdate = rootState.guest?.birthdate

      return dispatch("login")
    },
    async login({ dispatch, state, getters }) {
      const { hospitalCode, protocolNo, birthdate } = getters.patient

      if (!hospitalCode || !protocolNo || !birthdate) return this_.$router.push("/")

      const response = await axios()
        .post("/api/kochealthcare/login", { hospitalCode, protocolNo, birthdate })
        .catch(err => _$.error(err))

      if (!response || !response.isSuccess) return false

      state.sessionId = response.data.sessionId

      return dispatch("verify", { phoneNumber: response.data.phoneNumber })
    },
    async logout({ commit, getters }) {
      const { hospitalCode, protocolNo, birthdate, sessionId: id } = getters.patient

      const response = await axios()
        .post("/api/kochealthcare/logout", {
          session: {
            id,
            hospitalCode,
            protocolNo,
            birthdate
          }
        })
        .catch(err => _$.error(err))

      if (!response || !response.isSuccess) return false

      commit("patient", {})
      commit("auth", false)

      return true
    },
    async verify({ dispatch, commit, getters }, payload) {
      const result = await _$.vue.$interact.prompt(
        _$.vue.$t("SMS Verification"),
        _$.vue.$t("kochealthcare.pincode.message", [payload.phoneNumber]),
        "right",
        [{ type: "input", id: "pin", fluid: true, label: _$.vue.$t("SMS Code") }]
      )
      if (!result) return this_.$router.push("/")

      const { hospitalCode, protocolNo, birthdate, sessionId } = getters.patient
      const response = await axios()
        .post("/api/kochealthcare/verify", { hospitalCode, protocolNo, birthdate, sessionId, smsCode: result.pin.value })
        .catch(err => _$.error(err))

      if (!response || !response.isSuccess) return dispatch("verify", payload)

      commit("auth", true)
      return true
    },
    async reports({ commit, getters }, payload) {
      const { hospitalCode, protocolNo, birthdate, sessionId: id } = getters.patient

      const response = await axios()
        .post("/api/kochealthcare/reports", {
          session: {
            id,
            hospitalCode,
            protocolNo,
            birthdate
          },
          data: {
            pageSize: 0,
            page: 1,
            search: "",
            startDate: this_.$moment("1970-01-01T00:00:00").format(),
            endDate: this_
              .$moment()
              .add(1, "day")
              .format("YYYY-MM-DDTHH:mm:ss"),
            sortType: 1
          },
          type: payload.type
        })
        .catch(err => _$.error(err))

      if (!response || !response.isSuccess) return commit(payload.type, [])

      commit(payload.type, response.data)
      return true
    },
    async report({ getters }, payload) {
      const { hospitalCode, protocolNo, birthdate, sessionId: id } = getters.patient

      const response = await axios()
        .post("/api/kochealthcare/report", {
          session: {
            id,
            hospitalCode,
            protocolNo,
            birthdate
          },
          data: payload
        })
        .catch(err => _$.error(err))

      if (!response || !Array.isArray(response)) return false

      return response
    }
  }
}
